import { TrueBase, css, html } from "../TrueBase/TrueBase";
import "../TrueImageResponsive/TrueImageResponsive";

/**
 * @customelement - true-youtube-video
 * @description
 * Component that renders youtube video
 *
 * ### Properties
 *
 *
 */
export class TrueYoutubeVideo extends TrueBase {
  static get properties() {
    return {
      videoId: {
        type: String,
      },
      domRefImg: {
        type: Object,
        reflect: true,
        attribute: false,
      },
      thumbnail: {
        type: String,
      },
      domRefFrame: {
        attribute: false,
        reflect: true,
      },
      domRefPlayButton: {
        attribute: false,
        reflect: true,
      },
      iframeLoaded: {
        attribute: false,
        reflect: true,
      },
      autoplay: {
        type: Boolean,
        reflect: true,
      },
      videoTitle: {
        type: String,
      },
      posterUrlJpeg: {
        type: String,
        reflect: false,
        attribute: false,
      },
      focalpointLandscape: {
        type: String,
      },
      focalpointPortrait: {
        type: String,
      },
      focalpointSquare: {
        type: String,
      },
      focalpointWide: {
        type: String,
      },
      focalpointTall: {
        type: String,
      },
      focalpointDefault: {
        type: String,
      },
      cacheBusterValue: {
        type: String,
      },
      altText: {
        type: String,
      },
    };
  }

  constructor() {
    super();
    this.iframeLoaded = false;
    this.domRefImg = {
      fallback: HTMLImageElement,
      webp: HTMLSourceElement,
      jpeg: HTMLSourceElement,
    };
    this.domRefFrame = HTMLDivElement;
  }

  firstUpdated() {
    this.domRefFrame = this.shadowRoot.querySelector("#frame") || "";

    this.domRefImg = {
      fallback: this.shadowRoot.querySelector("#fallbackPlaceholder"),
      //webp: this.shadowRoot.querySelector("#webpPlaceholder"),
      //jpeg: this.shadowRoot.querySelector("#jpegPlaceholder"),
    };

    this.domRefPlayButton = this.shadowRoot.querySelector(".yt-playbtn");

    this._setupComponent();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  get videoPlay() {
    return this.getAttribute("videoPlay") || "Play";
  }

  set videoPlay(name) {
    this.setAttribute("videoPlay", name);
  }

  get videoStartAt() {
    return Number(this.getAttribute("videoStartAt") || "0");
  }

  set videoStartAt(time) {
    this.setAttribute("videoStartAt", String(time));
  }

  get autoplay() {
    return this.hasAttribute("autoplay");
  }

  set autoplay(value) {
    if (value) {
      this.setAttribute("autoplay", "");
    } else {
      this.removeAttribute("autoplay");
    }
  }

  get params() {
    return `start=${this.videoStartAt}&${this.getAttribute("params")}`;
  }

  render() {
    return html`
      <div id="frame" @click="${this._playVideo}">
        <true-image-responsive
          sizing="cover"
          imageSrc="${this.posterUrlJpeg}"
          id="fallbackPlaceholder"
          altText="${this.altText}"
          focalpointDefault="${this.focalpointDefault}"
          focalpointLandscape="${this.focalpointLandscape}"
          focalpointPortrait="${this.focalpointPortrait}"
          focalpointTall="${this.focalpointTall}"
          focalpointSquare="${this.focalpointSquare}"
          focalpointWide="${this.focalpointWide}"
          cacheBusterValue="${this.cacheBusterValue}"
        ></true-image-responsive>
        <button class="yt-playbtn"></button>
      </div>
    `;
  }

  _playVideo() {
    // if we have a previous iframe, remove it and the activated class
    if (this.domRefFrame.classList.contains("yt-activated")) {
      this.domRefFrame.classList.remove("yt-activated");
      this.shadowRoot.querySelector("iframe").remove();
      this.iframeLoaded = false;
    }

    this._addIframe();
  }

  /**
   * Parse our attributes and fire up some placeholders
   */
  _setupComponent() {
    this._initImagePlaceholder();

    if (videoTitle != "") {
      this.domRefPlayButton.setAttribute(
        "aria-label",
        `${this.videoPlay}: ${this.videoTitle}`
      );
      this.setAttribute("title", `${this.videoPlay}: ${this.videoTitle}`);
    }
    if (this.autoplay) {
      this._initIntersectionObserver();
    }
  }

  /**
   * Inject the iframe into the component body
   */
  _addIframe() {
    if (!this.iframeLoaded) {
      const iframeHTML = `
                <iframe frameborder="0" id="video-iframe"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen
                src="https://www.youtube-nocookie.com/embed/${this.videoId}?autoplay=1&${this.params}&rel=0&enablejsapi=1"
                ></iframe>`;
      this.domRefFrame.insertAdjacentHTML("beforeend", iframeHTML);
      this.domRefFrame.classList.add("yt-activated");
      this.iframeLoaded = true;
    }
  }

  /**
   * Setup the placeholder image for the component
   */
  _initImagePlaceholder() {
    if (this.thumbnail !== null && typeof this.thumbnail !== "undefined") {
      this.posterUrlJpeg = this.thumbnail;
    } else {
      // we don't know which image type to preload, so warm the connection
      TrueYoutubeVideo.addPrefetch("preconnect", "https://i.ytimg.com/");
      //posterUrlWebp = `https://i.ytimg.com/vi_webp/${this.videoId}/hqdefault.webp`;
      this.posterUrlJpeg = `https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg`;
    }

    //this.domRefImg.webp.srcset = posterUrlWebp || "";
    //this.domRefImg.jpeg.srcset = posterUrlJpeg;
    this.domRefImg.fallback.imageSrc = this.posterUrlJpeg;
    this.domRefImg.fallback.setAttribute(
      "aria-label",
      `${this.videoPlay}: ${this.videoTitle}`
    );
    this.domRefImg.fallback.setAttribute(
      "altText",
      `${this.videoPlay}: ${this.videoTitle}`
    );
  }

  /**
   * Setup the Intersection Observer to load the iframe when scrolled into view
   */
  _initIntersectionObserver() {
    if (
      "IntersectionObserver" in window &&
      "IntersectionObserverEntry" in window
    ) {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0,
      };

      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.iframeLoaded) {
            TrueYoutubeVideo.warmConnections();
            this._addIframe();
            observer.unobserve(this);
          }
        });
      }, options);

      observer.observe(this);
    }
  }

  /**
   * Add a <link rel={preload | preconnect} ...> to the head
   * @param {*} kind
   * @param {*} url
   * @param {*} as
   */
  static addPrefetch(kind, url, as) {
    const linkElem = document.createElement("link");
    linkElem.rel = kind;
    linkElem.href = url;
    if (as) {
      linkElem.as = as;
    }
    linkElem.crossOrigin = "true";
    document.head.append(linkElem);
  }

  /**
   * Begin preconnecting to warm up the iframe load Since the embed's netwok
   * requests load within its iframe, preload/prefetch'ing them outside the
   * iframe will only cause double-downloads. So, the best we can do is warm up
   * a few connections to origins that are in the critical path.
   *
   * Maybe `<link rel=preload as=document>` would work, but it's unsupported:
   * http://crbug.com/593267 But TBH, I don't think it'll happen soon with Site
   * Isolation and split caches adding serious complexity.
   */
  static warmConnections() {
    if (TrueYoutubeVideo.preconnected) return;
    // Host that YT uses to serve JS needed by player, per amp-youtube
    TrueYoutubeVideo.addPrefetch("preconnect", "https://s.ytimg.com");

    // The iframe document and most of its subresources come right off
    // youtube.com
    TrueYoutubeVideo.addPrefetch("preconnect", "https://www.youtube.com");

    // The botguard script is fetched off from google.com
    TrueYoutubeVideo.addPrefetch("preconnect", "https://www.google.com");

    // TODO: Not certain if these ad related domains are in the critical path.
    // Could verify with domain-specific throttling.
    TrueYoutubeVideo.addPrefetch(
      "preconnect",
      "https://googleads.g.doubleclick.net"
    );
    TrueYoutubeVideo.addPrefetch(
      "preconnect",
      "https://static.doubleclick.net"
    );
    TrueYoutubeVideo.preconnected = true;
  }

  static get styles() {
    return css`
      :host {
        contain: content;
        display: block;
        position: relative;
        width: 100%;
        height: 100%;
      }

      #frame {
        cursor: pointer;
        width: 100%;
        height: 100%;
        min-height: var(--youtube-video-min-height, 400px);
      }

      iframe {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
      }

      /* play button */
      .yt-playbtn {
        width: var(--youtube-video-play-button-width, 70px);
        height: var(--youtube-video-play-button-height, 46px);
        background-color: var(--youtube-video-play-button-background, #212121);
        z-index: 2;
        opacity: var(--youtube-video-play-button-opacity, 0.8);
        border-radius: var(--youtube-video-play-button-border-radius, 14%);
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
        border: var(--youtube-video-play-button-border, 0);
        cursor: pointer;
      }
      #frame:hover .yt-playbtn {
        background-color: var(
          --youtube-video-hover-play-button-background,
          #f00
        );
        opacity: var(--youtube-video-hover-play-button-opacity, 1);
      }

      /* play button triangle */
      .yt-playbtn:before {
        content: "";
        border-style: solid;
        border-width: var(
          --youtube-video-play-button-border-width,
          11px 0 11px 19px
        );
        border-color: var(
          --youtube-video-play-button-border-color,
          transparent transparent transparent #fff
        );
      }

      #frame:hover .yt-playbtn:before {
        border-color: var(
          --youtube-video-hover-play-button-border-color,
          transparent transparent transparent #fff
        );
      }
      .yt-playbtn,
      .yt-playbtn:before {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate3d(-50%, -50%, 0);
      }
      /* Post-click styles */
      .yt-activated {
        cursor: unset;
      }
      .yt-activated .yt-playbtn,
      .yt-activated true-image-responsive {
        display: none;
      }
    `;
  }
}

window.customElements.get("true-youtube-video") ||
  window.customElements.define("true-youtube-video", TrueYoutubeVideo);
